<template>
  <main
    v-style:bgv="{ md: `#fff url(${backgroundImage.src.md}) no-repeat top center/cover` }"
    class="md:py-16"
  >
    <div
      v-style="{
        h: { sm: '100vw', md: 'auto' },
        bgv: { sm: `#fff url(${backgroundImage.src.sm}) no-repeat center/cover`, md: 'none' },
      }"
      class="text-center container <md:flex <md:items-end <md:center <md:py-6"
    >
      <h1 class="bg-white p-2 title-2 uppercase i-block">
        {{ headerTitle }}
      </h1>
    </div>

    <div class="text-center container">
      <p class="mt-6 bg-white p-2 i-block">
        {{ introBody }}
      </p>
    </div>

    <div
      v-style:w="{ md: '38rem' }"
      class="mx-4 my-8 bg-white md:mx-a"
    >
      <div v-if="formState === FormState.INITIAL" class="p-6 shadow-sm md:p-8">
        <h2 class="mb-4 title-4 uppercase <md:text-center md:title-3">
          {{ formSection.title }}
        </h2>

        <p class="mb-6 <md:text-center">
          {{ formSection.intro }}
        </p>

        <form class="space-y-4" @submit.prevent="onFormSubmit">
          <vf-input v-model.trim="productSku" :placeholder="formSection.inputProductSkuPlaceholder">
            {{ formSection.inputProductSkuLabel }}

            <template #end>
              <vf-toggletip>
                <template #trigger>
                  <span class="text-xs">
                    <vf-icon name="info" />
                  </span>
                </template>

                {{ formSection.inputProductSkuTooltip }}
              </vf-toggletip>
            </template>
          </vf-input>

          <vf-input v-model.trim="productName" :placeholder="formSection.inputProductNamePlaceholder">
            {{ formSection.inputProductNameLabel }}

            <template #end>
              <vf-toggletip>
                <template #trigger>
                  <span class="text-xs">
                    <vf-icon name="info" />
                  </span>
                </template>

                {{ formSection.inputProductNameTooltip }}
              </vf-toggletip>
            </template>
          </vf-input>

          <vf-input v-model.trim="productDateCode" :disabled="!productSku && !productName" :placeholder="formSection.inputProductDateCodePlaceholder">
            {{ formSection.inputProductDateCodeLabel }}

            <template #end>
              <vf-toggletip>
                <template #trigger>
                  <span class="text-xs">
                    <vf-icon name="info" />
                  </span>
                </template>

                {{ formSection.inputProductDateCodeTooltip }}
              </vf-toggletip>
            </template>
          </vf-input>

          <div v-if="errorMessage" class="text-center text-xs c-red-30">
            {{ errorMessage }}
          </div>

          <vf-button class="w-full" type="submit">
            {{ formSection.confirm }}
          </vf-button>
        </form>
      </div>

      <div v-if="formState === FormState.RESULT" class="lg:shadow-lg">
        <div class="flex bg-black between">
          <span class="p-4 c-white uppercase">
            {{ filteredAssets.length }} {{ $pluralize(resultSection.resultsFound, filteredAssets.length) }}
          </span>

          <vf-button class="bg-black uppercase <md:hidden" @click="reset">
            <vf-icon name="refresh" size="md" />
            <span>{{ resultSection.reset }}</span>
          </vf-button>
        </div>

        <div v-style:max-h="{ md: '28rem' }" class="overflow-y-auto">
          <vf-table class="w-full">
            <!-- added an empty header as this table doesn't need one -->
            <template #head>
              <th class="hidden">
                header
              </th>
            </template>

            <tr
              v-for="({ name, sku, url }, key) in filteredAssets"
              :key
              class="text-sm"
              :class="{ 'bg-grey-80': key % 2 }"
            >
              <td class="p-4 pr-2">
                {{ sku }}
              </td>
              <td class="px-2 py-4">
                {{ name }}
              </td>
              <td class="pr-2 text-right">
                <!-- the file name ending with pdf.pdf is due the way it was uploaded to Cloudinary -->
                <vf-button size="xs" :to="url">
                  <span class="<md:hidden">{{ resultSection.viewPdf }}</span>
                  <span aria-hidden="true" class="md:hidden">PDF</span>
                </vf-button>
              </td>
            </tr>
          </vf-table>
        </div>

        <vf-button class="mx-a mt-6 f-col md:hidden" @click="reset">
          <vf-icon name="refresh" size="md" />
          <span>{{ resultSection.reset }}</span>
        </vf-button>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import type { CloudinaryAsset, DeclarationOfConformityAsset } from '#types/cloudinaryAsset'

const cloudinaryUrlPrefix = 'https://assets.vans.eu'

const { data: assets } = await useFetch(`${cloudinaryUrlPrefix}/any/list/declaration-of-conformity.json`, {
  transform: ({ resources }: { resources: CloudinaryAsset[] }): DeclarationOfConformityAsset[] => {
    return resources.map(({ format, public_id, resource_type, type, version }) => {
      /**
       * filename pattern
       * DOC_SKU_SEASON_DATECODE_NAME_...
       *
       * @example
       * DOC_VN00007E_SP19_XXXX_SQUARED-OFF
       * DOC_VN000LC0_SS24_0923_SPICOLI-4
       *
       */
      const parts = public_id.replace(/-/g, ' ').split('_')
      const [prefix, sku, , datecode, name] = parts

      const url = `${cloudinaryUrlPrefix}/${resource_type}/${type}/v${version}/${public_id}.${format}`

      return {
        datecode,
        name,
        prefix,
        sku,
        url
      }
    })
  }
})

if (!assets.value) throw createError({ fatal: true })

const { data, error } = await useApi().internal.getStaticPage('declaration-of-conformity')
if (!data.value) throw createError({ ...error.value!, fatal: true })

const { backgroundImage, formSection, headerTitle, introBody, resultSection } = data.value

const enum FormState {
  INITIAL,
  RESULT
}

const filteredAssets = ref<DeclarationOfConformityAsset[]>([])
const formState = ref<FormState>(FormState.INITIAL)
const errorMessage = ref('')
const productDateCode = ref('')
const productSku = ref('')
const productName = ref('')

const onFormSubmit = () => {
  errorMessage.value = ''

  if (!productSku.value && !productName.value) {
    if (productDateCode.value) {
      errorMessage.value = formSection.errorMessageProductCodeOrNameMissing
      return
    }

    errorMessage.value = formSection.errorMessageInputNotValid
    return
  }

  if (assets.value === null) assets.value = []

  const results = assets.value.filter(({ datecode, name, prefix, sku }) => {
    if (prefix.toLowerCase() !== 'doc') return false

    return (!productSku.value || sku.toLowerCase() === productSku.value.toLowerCase())
      && (!productName.value || name.toLowerCase().includes(productName.value.toLowerCase()))
      && (!productDateCode.value || datecode.toLowerCase() === productDateCode.value.toLowerCase())
  })

  if (!results.length) {
    errorMessage.value = formSection.errorMessageInputNotValid
    return
  }

  formState.value = FormState.RESULT
  filteredAssets.value = results
}

const reset = () => {
  filteredAssets.value = []
  formState.value = FormState.INITIAL
  productDateCode.value = ''
  productSku.value = ''
  productName.value = ''
}
</script>
